import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { devWarn } from "@/lib/log";
import axios from "axios";
import { API } from "@/lib/api";
import {
  ChatCircleDots,
  MagnifyingGlass,
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  Clock,
  Fire,
  ShoppingBag,
  Phone,
  EnvelopeSimple,
  ArrowsCounterClockwise,
  Tag,
  ShieldWarning,
  Prohibit,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABELS = {
  nuevo: { label: "Nuevo", color: "#1E4FCC", bg: "#E3F2FD" },
  interesado: { label: "Interesado", color: "#B45309", bg: "#FFF8E1" },
  negociando: { label: "Negociando", color: "#5D4037", bg: "#FFF3E0" },
  vendido: { label: "Vendido", color: "#1B5E20", bg: "#E8F5E9" },
  perdido: { label: "Perdido", color: "#B71C1C", bg: "#FFEBEE" },
};

export default function Conversaciones() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [convDetail, setConvDetail] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (statusFilter) q.append("status", statusFilter);
      const [{ data: list }, { data: s }] = await Promise.all([
        axios.get(`${API}/conversations?${q.toString()}`),
        axios.get(`${API}/conversations/stats`),
      ]);
      setItems(Array.isArray(list) ? list : []);
      setStats(s);
    } catch (e) {
      devWarn("load conversations failed", e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
    const t = setInterval(() => { load(); }, 30 * 1000); // refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  const loadDetail = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API}/conversations/${id}`);
      setConvDetail(data);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      toast.error("No se pudo abrir la conversación");
    }
  }, []);

  useEffect(() => {
    if (selected) {
      loadDetail(selected);
    } else {
      setConvDetail((prev) => (prev === null ? prev : null));
    }
  }, [selected, loadDetail]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const blob = [
        it.product_nombre,
        it.visitor_name,
        it.visitor_phone,
        it.visitor_email,
        it.last_message,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [items, search]);

  const sendReply = async (e) => {
    e?.preventDefault?.();
    const text = replyText.trim();
    if (!text || !selected || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${selected}/reply`, { text });
      setReplyText("");
      await Promise.all([loadDetail(selected), load()]);
      toast.success("Mensaje enviado");
    } catch (e) {
      toast.error("No se pudo enviar el mensaje");
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status) => {
    if (!selected) return;
    try {
      await axios.put(`${API}/conversations/${selected}`, { status });
      await Promise.all([loadDetail(selected), load()]);
      toast.success(`Estado: ${STATUS_LABELS[status]?.label || status}`);
    } catch {
      toast.error("No se pudo cambiar el estado");
    }
  };

  const markSpam = async () => {
    if (!selected) return;
    if (!window.confirm("¿Marcar esta charla como spam? Se cerrará y la IA aprenderá a bloquear futuros mensajes idénticos sin gastar tokens.")) return;
    try {
      const { data } = await axios.post(`${API}/conversations/${selected}/mark-spam`);
      await Promise.all([loadDetail(selected), load()]);
      if (data?.pattern_saved) {
        toast.success("Marcada como spam. La IA aprendió este patrón.");
      } else {
        toast.info("Cerrada como perdida (sin mensaje del visitante para aprender).");
      }
    } catch {
      toast.error("No se pudo marcar como spam");
    }
  };

  const resolveFollowup = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/conversations/${selected}/resolve-followup`);
      await Promise.all([loadDetail(selected), load()]);
      toast.success("Pendiente marcado como resuelto");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo marcar como resuelto");
    }
  };

  return (
    <div className="px-4 sm:px-6 md:px-10 py-8 max-w-7xl mx-auto" data-testid="conversaciones-page">
      <header className="mb-6">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
            Conversaciones
          </h1>
          <span className="text-sm text-muted2 font-medium">
            Charlas IA + seguimiento automático con tus posibles compradores
          </span>
        </div>
      </header>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <StatBox label="Total" value={stats.total} icon={ChatCircleDots} />
          <StatBox label="Alta intención" value={stats.high_intent} icon={Fire} color="#FF3D00" />
          <StatBox label="Negociando" value={stats.by_status?.negociando || 0} icon={Clock} color="#5D4037" />
          <StatBox label="Vendidos" value={stats.by_status?.vendido || 0} icon={CheckCircle} color="#1B5E20" />
          <StatBox label="Sin respuesta" value={stats.needs_reply} icon={ArrowsCounterClockwise} color="#B45309" />
        </div>
      )}

      <TopSpammersPanel />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* List column */}
        <section className="lg:col-span-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted2" />
              <input
                type="text"
                placeholder="Buscar producto, nombre, teléfono…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
                data-testid="conv-search"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
              data-testid="conv-filter-status"
            >
              <option value="">Todos</option>
              <option value="nuevo">Nuevo</option>
              <option value="interesado">Interesado</option>
              <option value="negociando">Negociando</option>
              <option value="vendido">Vendido</option>
              <option value="perdido">Perdido</option>
            </select>
          </div>

          <div className="border border-zinc-200 bg-white max-h-[calc(100vh-260px)] overflow-y-auto">
            {loading ? (
              <div className="text-center text-sm text-muted2 py-12">Cargando…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 px-4">
                <ChatCircleDots size={36} className="mx-auto text-klein/40 mb-3" weight="duotone" />
                <p className="text-sm font-medium">No hay conversaciones todavía</p>
                <p className="text-xs text-muted2 mt-1">
                  Cuando un visitante use el chat en una página pública, aparecerá aquí.
                </p>
              </div>
            ) : (
              filtered.map((it) => (
                <ConvRow
                  key={it.id}
                  item={it}
                  active={selected === it.id}
                  onClick={() => setSelected(it.id)}
                />
              ))
            )}
          </div>
        </section>

        {/* Detail column */}
        <section className="lg:col-span-7">
          {!convDetail ? (
            <div className="border border-dashed border-zinc-300 bg-zinc-50 p-12 text-center min-h-[400px] flex flex-col items-center justify-center">
              <ChatCircleDots size={48} className="text-klein/30 mb-4" weight="duotone" />
              <p className="text-sm text-muted2">
                Selecciona una conversación para ver el hilo completo y responder.
              </p>
            </div>
          ) : (
            <ConvDetail
              conv={convDetail}
              onSend={sendReply}
              replyText={replyText}
              setReplyText={setReplyText}
              sending={sending}
              scrollRef={scrollRef}
              onChangeStatus={changeStatus}
              onMarkSpam={markSpam}
              onResolveFollowup={resolveFollowup}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, color = "#1E4FCC" }) {
  return (
    <div className="p-3 border border-zinc-200 bg-white">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted2 font-semibold">
        <Icon size={12} weight="fill" style={{ color }} />
        {label}
      </div>
      <div className="h-display text-2xl font-bold tabular-nums mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function ConvRow({ item, active, onClick }) {
  const cfg = STATUS_LABELS[item.status] || STATUS_LABELS.nuevo;
  const intent = item.intent_score || 0;
  const intentColor = intent >= 70 ? "#FF3D00" : intent >= 40 ? "#B45309" : "#71717A";
  const name = item.visitor_name || "Anónimo";
  const contact = item.visitor_phone || item.visitor_email;
  const pending = item.pending_followup && !item.pending_followup.resolved ? item.pending_followup : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors block ${
        active ? "bg-klein/5 border-l-[3px] border-l-klein" : "border-l-[3px] border-l-transparent"
      }`}
      data-testid={`conv-row-${item.id}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5"
          style={{ color: cfg.color, backgroundColor: cfg.bg }}
        >
          {cfg.label}
        </span>
        {pending && (
          <span
            className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 bg-[#FF2A00] text-white"
            data-testid={`conv-pending-badge-${item.id}`}
          >
            Acción pendiente
          </span>
        )}
        {intent >= 40 && (
          <span
            className="text-[10px] font-semibold inline-flex items-center gap-0.5"
            style={{ color: intentColor }}
          >
            <Fire size={11} weight="fill" /> {intent}
          </span>
        )}
        <span className="ml-auto text-[10px] text-muted2 tabular-nums">{relTime(item.last_message_at)}</span>
      </div>
      <div className="text-sm font-semibold text-ink truncate">{name}</div>
      <div className="text-xs text-muted2 truncate">{item.product_nombre}</div>
      {contact && (
        <div className="text-[11px] text-muted2 mt-0.5 truncate">
          {item.visitor_phone ? <Phone size={11} weight="bold" className="inline mr-1" /> : <EnvelopeSimple size={11} weight="bold" className="inline mr-1" />}
          {contact}
        </div>
      )}
      {pending ? (
        <div className="text-xs text-[#B71C1C] mt-1 truncate italic font-medium">
          ❓ {pending.question}
        </div>
      ) : (
        <div className="text-xs text-zinc-600 mt-1 truncate italic">
          {item.last_message_role === "visitor" ? "👤 " : item.last_message_role === "admin" ? "🧑‍💼 " : "🤖 "}
          {item.last_message || "(sin mensajes)"}
        </div>
      )}
    </button>
  );
}

function ConvDetail({ conv, onSend, replyText, setReplyText, sending, scrollRef, onChangeStatus, onMarkSpam, onResolveFollowup }) {
  const cfg = STATUS_LABELS[conv.status] || STATUS_LABELS.nuevo;
  const pending = conv.pending_followup && !conv.pending_followup.resolved ? conv.pending_followup : null;
  return (
    <div className="border border-zinc-200 bg-white flex flex-col min-h-[400px] max-h-[calc(100vh-220px)]">
      {pending && (
        <div
          className="px-4 py-3 bg-[#FFF3E0] border-b border-[#FFE0B2] flex items-start gap-3"
          data-testid="conv-pending-banner"
        >
          <span className="text-xl leading-none">❓</span>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wide font-bold text-[#B71C1C]">
              Acción pendiente · responde en WhatsApp
            </div>
            <p className="text-sm text-ink mt-0.5 leading-snug">{pending.question}</p>
            <p className="text-[11px] text-muted2 mt-1">
              La IA prometió confirmar con el dueño. Cuando ya respondiste al cliente, marca como resuelto para limpiar el badge.
            </p>
          </div>
          <button
            type="button"
            onClick={onResolveFollowup}
            className="shrink-0 inline-flex items-center gap-1 bg-klein hover:bg-klein-dark text-white text-xs font-semibold px-3 py-2"
            data-testid="conv-resolve-followup"
          >
            <CheckCircle size={14} weight="fill" />
            Marcar resuelto
          </button>
        </div>
      )}
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="text-sm font-bold text-ink truncate">{conv.visitor_name || "Visitante anónimo"}</div>
            <div className="text-xs text-muted2 truncate">{conv.product_nombre}</div>
            <div className="text-[11px] text-muted2 mt-1 flex flex-wrap gap-x-3">
              {conv.visitor_phone && (
                <span><Phone size={11} weight="bold" className="inline mr-0.5" /> {conv.visitor_phone}</span>
              )}
              {conv.visitor_email && (
                <span><EnvelopeSimple size={11} weight="bold" className="inline mr-0.5" /> {conv.visitor_email}</span>
              )}
              {conv.discount_offered_pct > 0 && (
                <span className="text-[#1B5E20] font-medium">
                  <Tag size={11} weight="fill" className="inline mr-0.5" /> {conv.discount_offered_pct}% off
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <span
              className="inline-block text-[10px] uppercase tracking-wide font-semibold px-2 py-1 mb-1"
              style={{ color: cfg.color, backgroundColor: cfg.bg }}
            >
              {cfg.label}
            </span>
            <div className="text-[11px] tabular-nums" style={{ color: cfg.color }}>
              Intent: <strong>{conv.intent_score || 0}</strong>/100
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {Object.entries(STATUS_LABELS).map(([key, c]) => (
            <button
              key={key}
              type="button"
              onClick={() => onChangeStatus(key)}
              disabled={conv.status === key}
              className="text-[10px] uppercase tracking-wide font-medium px-2 py-1 border disabled:opacity-50 disabled:cursor-default hover:bg-zinc-100"
              style={{
                color: conv.status === key ? c.color : "#71717A",
                borderColor: conv.status === key ? c.color : "#E4E4E7",
              }}
              data-testid={`set-status-${key}`}
            >
              {c.label}
            </button>
          ))}
          {!conv.is_spam && (
            <button
              type="button"
              onClick={onMarkSpam}
              className="text-[10px] uppercase tracking-wide font-medium px-2 py-1 border border-[#FFCDD2] text-[#B71C1C] hover:bg-[#FFEBEE] ml-auto"
              data-testid="mark-spam"
              title="Cierra la charla, la marca como perdida y la IA aprenderá a bloquear mensajes idénticos."
            >
              🚫 Marcar spam
            </button>
          )}
          {conv.is_spam && (
            <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-1 bg-[#FFEBEE] text-[#B71C1C] border border-[#FFCDD2] ml-auto">
              🚫 Spam aprendido
            </span>
          )}
        </div>
        {conv.internal_note && (
          <div className="mt-2 px-2 py-1.5 text-[11px] bg-[#FFF8E1] border-l-2 border-[#B45309] text-[#5D4037]">
            <strong>Nota IA:</strong> {conv.internal_note}
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-white">
        {(conv.messages || []).map((m, i) => (
          <DetailBubble key={`${m.created_at}-${i}`} msg={m} />
        ))}
        {(conv.messages || []).length === 0 && (
          <div className="text-center text-sm text-muted2 py-12">Sin mensajes aún.</div>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={onSend} className="px-3 py-3 border-t border-zinc-200 bg-zinc-50">
        <div className="flex items-end gap-2">
          <textarea
            rows={2}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Responde como vendedor (interrumpe a la IA en esta charla)…"
            className="flex-1 px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none resize-none"
            data-testid="reply-input"
          />
          <button
            type="submit"
            disabled={sending || !replyText.trim()}
            className="px-3 py-2 bg-klein text-white text-sm font-semibold hover:bg-klein-dark disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="reply-send"
          >
            <PaperPlaneTilt size={14} weight="fill" /> Enviar
          </button>
        </div>
        <p className="mt-1.5 text-[10px] text-muted2">
          También puedes responder rapidísimo desde Telegram con <code className="px-1 bg-zinc-100">/r {conv.id?.slice(0, 8)} tu mensaje</code>
        </p>
      </form>
    </div>
  );
}

function DetailBubble({ msg }) {
  const role = msg.role;
  const isVisitor = role === "visitor";
  const isAI = role === "ai";
  const isAdmin = role === "admin";
  const label = isVisitor ? "Cliente" : isAI ? "Asistente IA" : "Vendedor";
  const time = formatDateTime(msg.created_at);
  const isFollowup = msg.is_followup;

  return (
    <div className={`flex ${isVisitor ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[78%] ${isVisitor ? "items-end text-right" : ""}`}>
        <div className="text-[10px] uppercase tracking-wide font-semibold mb-0.5"
          style={{ color: isVisitor ? "#1E4FCC" : isAdmin ? "#B45309" : "#5D4037" }}
        >
          {label} {isFollowup ? `· follow-up ${msg.followup_state}/3` : ""}
        </div>
        <div
          className={`px-3 py-2 text-sm whitespace-pre-wrap break-words ${
            isVisitor
              ? "bg-klein text-white"
              : isAdmin
              ? "bg-[#FFF3E0] text-ink border border-[#FFCC80]"
              : isFollowup
              ? "bg-[#FFF8E1] text-ink border-l-2 border-[#B45309]"
              : "bg-zinc-100 text-ink"
          }`}
        >
          {msg.text}
        </div>
        <div className="text-[10px] text-muted2 mt-0.5">{time}</div>
      </div>
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "ahora";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const days = Math.floor(h / 24);
    return `${days}d`;
  } catch {
    return "";
  }
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function TopSpammersPanel() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [busyIp, setBusyIp] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/spam/top-ips?limit=25&days=${days}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      devWarn("top-ips load failed", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const blockIp = async (ip) => {
    if (!ip) return;
    setBusyIp(ip);
    try {
      await axios.post(`${API}/spam/block-ip`, { ip, note: "Bloqueada desde Top spammers" });
      toast.success(`IP ${ip} bloqueada`);
      await load();
    } catch (e) {
      toast.error("No se pudo bloquear la IP");
    } finally {
      setBusyIp(null);
    }
  };

  const unblockIp = async (ip) => {
    if (!ip) return;
    if (!window.confirm(`¿Desbloquear ${ip}? Volverá a poder iniciar chats.`)) return;
    setBusyIp(ip);
    try {
      await axios.delete(`${API}/spam/block-ip/${encodeURIComponent(ip)}`);
      toast.success(`IP ${ip} desbloqueada`);
      await load();
    } catch (e) {
      toast.error("No se pudo desbloquear la IP");
    } finally {
      setBusyIp(null);
    }
  };

  const totalHits = useMemo(
    () => rows.reduce((acc, r) => acc + (r.hits || 0), 0),
    [rows],
  );
  const blockedCount = useMemo(
    () => rows.filter((r) => r.blocked).length,
    [rows],
  );

  return (
    <section className="mb-6 border border-zinc-200 bg-white" data-testid="top-spammers-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-zinc-50 hover:bg-zinc-100 border-b border-zinc-200"
        data-testid="top-spammers-toggle"
      >
        <ShieldWarning size={20} weight="duotone" className="text-[#B71C1C]" />
        <div className="text-left flex-1">
          <div className="text-sm font-bold text-ink">Top spammers</div>
          <div className="text-[11px] text-muted2">
            IPs que más han disparado el detector de spam · {rows.length} IPs · {totalHits} intentos · {blockedCount} bloqueadas
          </div>
        </div>
        {open ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
      </button>

      {open && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <label className="text-[11px] uppercase tracking-wide font-semibold text-muted2">Ventana:</label>
            {[7, 30, 90, 365].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`text-[11px] px-2 py-1 border ${
                  days === d
                    ? "border-klein text-klein bg-klein/5 font-semibold"
                    : "border-zinc-300 text-muted2 hover:bg-zinc-50"
                }`}
                data-testid={`top-spammers-days-${d}`}
              >
                {d === 365 ? "1 año" : `${d}d`}
              </button>
            ))}
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="ml-auto text-[11px] px-2 py-1 border border-zinc-300 text-muted2 hover:bg-zinc-50 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="top-spammers-refresh"
            >
              <ArrowsCounterClockwise size={11} weight="bold" /> Refrescar
            </button>
          </div>

          {loading ? (
            <div className="text-center text-sm text-muted2 py-8">Cargando…</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8">
              <ShieldWarning size={32} weight="duotone" className="mx-auto text-[#1B5E20]/30 mb-2" />
              <p className="text-sm font-medium text-ink">Sin spammers en esta ventana</p>
              <p className="text-[11px] text-muted2 mt-1">El anti-spam está limpio. Buen trabajo.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-muted2 font-semibold border-b border-zinc-200">
                    <th className="py-2 pr-3">IP</th>
                    <th className="py-2 pr-3 text-right">Intentos</th>
                    <th className="py-2 pr-3 text-right">Patrones</th>
                    <th className="py-2 pr-3 text-right">Productos</th>
                    <th className="py-2 pr-3">Último intento</th>
                    <th className="py-2 pr-3">Muestra</th>
                    <th className="py-2 pr-3 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.ip}
                      className="border-b border-zinc-100 hover:bg-zinc-50"
                      data-testid={`top-spammer-${r.ip}`}
                    >
                      <td className="py-2 pr-3 font-mono text-[11px] tabular-nums">
                        <div className="flex items-center gap-1.5">
                          {r.blocked && (
                            <span
                              className="text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 bg-[#FFEBEE] text-[#B71C1C] border border-[#FFCDD2]"
                              title="IP bloqueada"
                            >
                              bloqueada
                            </span>
                          )}
                          <span className="break-all">{r.ip}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums font-bold text-[#B71C1C]">
                        {r.hits}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {r.distinct_patterns}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums" title={(r.products_sample || []).join(", ")}>
                        {r.products_targeted}
                      </td>
                      <td className="py-2 pr-3 tabular-nums text-muted2">
                        {formatDateTime(r.last_attempt_at)}
                      </td>
                      <td className="py-2 pr-3 max-w-[260px]">
                        <div className="truncate italic text-zinc-600" title={r.sample_text}>
                          {r.sample_text || "(sin texto)"}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {r.blocked ? (
                          <button
                            type="button"
                            onClick={() => unblockIp(r.ip)}
                            disabled={busyIp === r.ip}
                            className="text-[10px] uppercase tracking-wide font-semibold px-2 py-1 border border-zinc-300 text-muted2 hover:bg-zinc-50 disabled:opacity-50"
                            data-testid={`unblock-ip-${r.ip}`}
                          >
                            Desbloquear
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => blockIp(r.ip)}
                            disabled={busyIp === r.ip}
                            className="text-[10px] uppercase tracking-wide font-semibold px-2 py-1 border border-[#FFCDD2] text-[#B71C1C] hover:bg-[#FFEBEE] disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid={`block-ip-${r.ip}`}
                          >
                            <Prohibit size={11} weight="bold" /> Bloquear
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-3 text-[10px] text-muted2 leading-relaxed">
            Bloquear una IP detiene de inmediato sus intentos en <code className="px-1 bg-zinc-100">/conversations/start</code> y <code className="px-1 bg-zinc-100">/conversations/message</code> — sin gastar tokens de IA ni alertas de Telegram. Si bloqueas por error, vuelve a desbloquear aquí mismo.
          </p>
        </div>
      )}
    </section>
  );
}
